import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import SavingOverlay from '../SavingOverlay';
import { colors, typography } from '../../theme/designSystem';
import { getDefaultInputLabelProps, getDefaultTextFieldSx, getMultilineInputLabelProps, getMultilineTextFieldSx } from '../../theme/textFieldStyles';
import { ContractCustomField } from '../../services/contractTemplatesService';
import { contractIssueErrorMessage, contractsService, ComposeContractPayload, ContractSubject, IssuableContractTemplate } from '../../services/contractsService';

/**
 * Emitir contrato (aba Contratos do paciente ou página do profissional): escolhe a versão publicada
 * de um modelo do público certo, informa data e vigência e completa os campos personalizados que o
 * modelo definiu. O texto final nasce no servidor — a prévia aqui é o mesmo texto que será gravado.
 * Não há edição livre: o que muda de contratante para contratante são os campos; o resto é a versão
 * publicada, que a pessoa aceitou como está.
 */
interface Props {
  open: boolean;
  subject: ContractSubject;
  templates: IssuableContractTemplate[];
  saving?: boolean;
  onClose: () => void;
  onIssued: (id: string) => void;
}

const today = () => new Date().toISOString().slice(0, 10);

const IssueContractModal: React.FC<Props> = ({ open, subject, templates, saving = false, onClose, onIssued }) => {
  const [versionId, setVersionId] = useState('');
  const [date, setDate] = useState(today());
  const [validFrom, setValidFrom] = useState(today());
  const [validTo, setValidTo] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVersionId(templates.length === 1 ? templates[0].versionId : '');
    setDate(today());
    setValidFrom(today());
    setValidTo('');
    setValues({});
    setError('');
    setPreview(null);
    setPreviewOpen(false);
  }, [open, templates]);

  const template = useMemo(() => templates.find((t) => t.versionId === versionId) ?? null, [templates, versionId]);

  const payload = (): ComposeContractPayload => ({
    versionId,
    date,
    validFrom,
    validTo: validTo || null,
    fieldValues: Object.fromEntries((template?.customFields ?? []).map((f) => [f.key, values[f.key] ?? null])),
  });

  const validateLocal = (): string => {
    if (!versionId) return 'Escolha o modelo de contrato.';
    if (!date) return 'Informe a data do contrato.';
    if (!validFrom) return 'Informe o início da vigência.';
    if (validTo && validTo < validFrom) return 'A vigência final não pode ser anterior à inicial.';
    return '';
  };

  const setValue = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    // A prévia mostrava outro conjunto de valores: some até ser pedida de novo
    setPreview(null);
  };

  const loadPreview = async () => {
    const localError = validateLocal();
    if (localError) { setError(localError); return; }
    setError('');
    setPreviewLoading(true);
    try {
      setPreview(await contractsService.preview(subject, payload()));
      setPreviewOpen(true);
    } catch (e) {
      setError(contractIssueErrorMessage(e, 'Não foi possível montar a prévia.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const issue = async () => {
    const localError = validateLocal();
    if (localError) { setError(localError); return; }
    const missing = (template?.customFields ?? []).find((f) => f.required && !(values[f.key] ?? '').trim());
    if (missing) { setError(`O campo "${missing.label}" é obrigatório.`); return; }
    setError('');
    setIssuing(true);
    try {
      const id = await contractsService.issue(subject, payload());
      onIssued(id);
    } catch (e) {
      setError(contractIssueErrorMessage(e, 'Não foi possível emitir o contrato.'));
    } finally {
      setIssuing(false);
    }
  };

  const renderField = (field: ContractCustomField) => {
    const common = {
      key: field.key,
      size: 'small' as const,
      label: field.required ? `${field.label} *` : field.label,
      value: values[field.key] ?? '',
      helperText: field.help ?? undefined,
      InputLabelProps: getDefaultInputLabelProps(),
      sx: getDefaultTextFieldSx(),
      fullWidth: true,
    };
    switch (field.type) {
      case 'date':
        return <TextField {...common} type="date" onChange={(e) => setValue(field.key, e.target.value)} />;
      case 'number':
        return <TextField {...common} type="number" inputProps={{ step: 'any' }} onChange={(e) => setValue(field.key, e.target.value)} />;
      case 'money':
        return (
          <TextField
            {...common}
            type="number"
            inputProps={{ step: '0.01', min: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            onChange={(e) => setValue(field.key, e.target.value)}
          />
        );
      case 'option':
        return (
          <TextField {...common} select onChange={(e) => setValue(field.key, e.target.value)}>
            <MenuItem value="">Selecione</MenuItem>
            {(field.options ?? []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        );
      case 'long_text':
        return <TextField {...common} InputLabelProps={getMultilineInputLabelProps()} sx={getMultilineTextFieldSx()} multiline minRows={3} inputProps={{ maxLength: 4000 }} onChange={(e) => setValue(field.key, e.target.value)} />;
      default:
        return <TextField {...common} inputProps={{ maxLength: 4000 }} onChange={(e) => setValue(field.key, e.target.value)} />;
    }
  };

  const busy = saving || issuing;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative' } }}>
      <SavingOverlay open={busy} message="Emitindo contrato..." />
      <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem' }}>
        <Typography component="span" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold }}>Emitir contrato</Typography>
        <IconButton onClick={onClose} disabled={busy} sx={{ color: colors.white, padding: '0.25rem' }} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '1.5rem !important' }}>
        {templates.length === 0 ? (
          <Typography sx={{ color: colors.textSecondary }}>
            Nenhum modelo de contrato publicado para {subject.kind === 'professional' ? 'profissional' : 'paciente'}. Cadastre e publique um modelo com esse destinatário em Prontuário e documentos &gt; Modelos de contrato.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              size="small"
              label="Modelo de contrato"
              value={versionId}
              onChange={(e) => { setVersionId(e.target.value); setValues({}); setPreview(null); setError(''); }}
              InputLabelProps={getDefaultInputLabelProps()}
              sx={getDefaultTextFieldSx()}
              fullWidth
            >
              {templates.map((t) => (
                <MenuItem key={t.versionId} value={t.versionId}>{t.title} · v{t.version}</MenuItem>
              ))}
            </TextField>
            {template?.description && (
              <Typography sx={{ fontSize: '0.8rem', color: colors.textSecondary, mt: -1 }}>{template.description}</Typography>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
              <TextField size="small" type="date" label="Data do contrato" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }} InputLabelProps={getDefaultInputLabelProps()} sx={getDefaultTextFieldSx()} />
              <TextField size="small" type="date" label="Vigência: início" value={validFrom} onChange={(e) => { setValidFrom(e.target.value); setPreview(null); }} InputLabelProps={getDefaultInputLabelProps()} sx={getDefaultTextFieldSx()} />
              <TextField size="small" type="date" label="Vigência: fim" value={validTo} onChange={(e) => { setValidTo(e.target.value); setPreview(null); }} helperText="Vazio = prazo indeterminado" InputLabelProps={getDefaultInputLabelProps()} sx={getDefaultTextFieldSx()} />
            </Box>

            {template && template.customFields.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textStrong, mb: 1 }}>
                  Dados deste contrato
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                  {template.customFields.map((f) => (
                    <Box key={f.key} sx={{ gridColumn: f.type === 'long_text' ? { xs: 'auto', md: '1 / -1' } : 'auto' }}>
                      {renderField(f)}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {template && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {template.requiresSignature && (
                  <Chip label={subject.kind === 'professional' ? 'Exige assinatura do profissional' : 'Exige assinatura do responsável'} size="small" sx={{ backgroundColor: colors.warningSoft, color: colors.warningSoftText, fontWeight: 600 }} />
                )}
                {template.autoRenew && (
                  <Chip label="Renovação automática ao fim da vigência" size="small" sx={{ backgroundColor: colors.successSoft, color: colors.successHover, fontWeight: 600 }} />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={previewLoading ? <CircularProgress size={14} /> : previewOpen && preview ? <VisibilityOff /> : <Visibility />}
                  onClick={() => (preview && previewOpen ? setPreviewOpen(false) : loadPreview())}
                  disabled={previewLoading || busy}
                  sx={{ textTransform: 'none', borderColor: colors.primary, color: colors.primaryText, ml: 'auto' }}
                >
                  {preview && previewOpen ? 'Ocultar prévia' : 'Pré-visualizar'}
                </Button>
              </Box>
            )}

            <Collapse in={previewOpen && preview !== null}>
              <Box sx={{ border: `1px solid ${colors.border}`, borderRadius: '6px', p: { xs: 1.5, md: 2.5 }, backgroundColor: colors.white, color: '#000', fontSize: '0.9rem', lineHeight: 1.6, maxHeight: 360, overflow: 'auto', '& p': { m: 0, mb: 1 }, '& ul, & ol': { my: 1, pl: 3 } }}>
                <Typography variant="caption" sx={{ display: 'block', color: colors.textSecondary, mb: 1 }}>
                  Prévia do texto que será gravado. Lacunas (______) são dados que o cadastro não tem ou campos ainda vazios.
                </Typography>
                <Box dangerouslySetInnerHTML={{ __html: preview ?? '' }} />
              </Box>
            </Collapse>

            {error && <Typography sx={{ color: colors.error, fontSize: '0.85rem' }}>{error}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={busy} sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary }}>Cancelar</Button>
        <Button onClick={issue} variant="contained" disabled={busy || templates.length === 0 || !template} sx={{ textTransform: 'none', backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryHover } }}>
          Emitir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueContractModal;
